import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase';

const RewardsList = ({ rewards = [], onReload }) => {
  const getRewardTypeConfig = (type) => {
    const configs = {
      discount: {
        label: 'Discount',
        icon: 'Percent',
        color: 'var(--color-primary)'
      },
      free_month: {
        label: 'Free Month',
        icon: 'Calendar',
        color: 'var(--color-success)'
      },
      credit: {
        label: 'Credit',
        icon: 'DollarSign',
        color: 'var(--color-accent)'
      },
      merchandise: {
        label: 'Merchandise',
        icon: 'Gift',
        color: 'var(--color-warning)'
      },
      bonus_points: {
        label: 'Bonus Points',
        icon: 'Star',
        color: 'var(--color-primary)'
      }
    };
    return configs?.[type] || configs?.credit;
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        label: 'Pending',
        bgColor: 'bg-muted',
        textColor: 'text-muted-foreground'
      },
      earned: {
        label: 'Available',
        bgColor: 'bg-success/10',
        textColor: 'text-success'
      },
      claimed: {
        label: 'Claimed',
        bgColor: 'bg-primary/10',
        textColor: 'text-primary'
      },
      expired: {
        label: 'Expired',
        bgColor: 'bg-error/10',
        textColor: 'text-error'
      }
    };
    return configs?.[status] || configs?.pending;
  };

  const handleClaimReward = async (rewardId) => {
    try {
      const { error } = await supabase
        ?.from('referral_rewards')
        ?.update({
          status: 'claimed',
          claimed_at: new Date()?.toISOString()
        })
        ?.eq('id', rewardId);

      if (error) throw error;

      // Reload rewards list
      onReload?.();
    } catch (err) {
      console.error('Error claiming reward:', err);
    }
  };

  if (rewards?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="flex items-center justify-center h-16 w-16 rounded-full bg-muted">
            <Icon name="Gift" size={32} color="var(--color-muted-foreground)" />
          </div>
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No Rewards Yet</h3>
        <p className="text-sm text-muted-foreground">
          Earn rewards by successfully referring friends who become active members!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rewards?.map((reward) => {
        const typeConfig = getRewardTypeConfig(reward?.reward_type);
        const statusConfig = getStatusConfig(reward?.status);
        const isExpiringSoon = reward?.expires_at && 
          new Date(reward?.expires_at) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
        
        return (
          <div
            key={reward?.id}
            className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between gap-4">
              {/* Reward Info */}
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary/10">
                    <Icon name={typeConfig?.icon} size={24} color={typeConfig?.color} />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-foreground">
                      {typeConfig?.label}
                    </h4>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig?.bgColor} ${statusConfig?.textColor}`}>
                      {statusConfig?.label}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {reward?.reward_description}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    {reward?.reward_value > 0 && (
                      <div className="flex items-center gap-1 font-semibold text-accent">
                        <Icon name="DollarSign" size={12} />
                        <span>${parseFloat(reward?.reward_value)?.toFixed(2)}</span>
                      </div>
                    )}
                    {reward?.earned_at && (
                      <div className="flex items-center gap-1">
                        <Icon name="Calendar" size={12} />
                        <span>Earned {format(new Date(reward?.earned_at), 'MMM d, yyyy')}</span>
                      </div>
                    )}
                    {reward?.expires_at && (
                      <div className={`flex items-center gap-1 ${isExpiringSoon ? 'text-warning font-medium' : ''}`}>
                        <Icon name="Clock" size={12} />
                        <span>Expires {format(new Date(reward?.expires_at), 'MMM d, yyyy')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Button */}
              {reward?.status === 'earned' && (
                <div className="flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => handleClaimReward(reward?.id)}
                    iconName="Gift"
                  >
                    Claim
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RewardsList;